import React, { useCallback, useMemo } from 'react';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { DisplayInput, DisplayInputProps } from './DisplayInput';
import { DisplayTextarea, DisplayTextareaProps } from './DisplayTextarea';
import {
  Checkbox,
  CheckboxContent,
  CheckboxControl,
  CheckboxIndicator,
  CheckboxProps,
  Link,
  LinkProps
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useController } from 'react-hook-form';
import { FieldTimeInput } from './FieldTimeInput';
import { FieldTimeProps } from './Field';
import { FieldDatePicker, FieldDatePickerProps } from './FieldDatePicker';
import { parseDate } from '@internationalized/date';
import {
  DisplayNumberInput,
  DisplayNumberInputProps
} from './DisplayNumberInput';

// Types
export type DisplayAttachmentsProps = DisplayInputProps;
export type DisplayArrayProps = DisplayInputProps;
export type DisplayArrayReferenceProps = DisplayInputProps;
export type DisplayBooleanProps = CheckboxProps & {
  /**
   * The function to format the value before displaying it.
   */
  accessor?: (value: unknown) => boolean | null | undefined;
  /**
   * The path inside the form object.
   */
  path: string;
};
export type DisplayCurrencyProps = DisplayNumberInputProps;
export type DisplayDateTimeProps = FieldDatePickerProps;
export type DisplayDateProps = FieldDatePickerProps;
export type DisplayEnumProps = DisplayInputProps;
export type DisplayFloatProps = DisplayNumberInputProps;
export type DisplayImageProps = React.ComponentPropsWithRef<'img'> & {
  /**
   * The function to format the value before displaying it.
   */
  accessor?: (value: unknown) => string | null | undefined;
  /**
   * The path inside the form object.
   */
  path: string;
};
export type DisplayIntegerProps = DisplayNumberInputProps;
// Link's own children are a react-aria render prop; the value is rendered as
// the fallback when there are none, so this narrows them to what can stand in
// for it.
export type DisplayLinkProps = Omit<LinkProps, 'children'> & {
  /**
   * The function to format the value before displaying it.
   */
  accessor?: (value: string | null | undefined) => string | null | undefined;
  /**
   * The path inside the form object.
   */
  path: string;
  /**
   * Content to display when value is empty
   */
  empty?: React.ReactNode;
  children?: React.ReactNode;
};
export type DisplayReferenceProps = DisplayInputProps;
export type DisplayStringProps = DisplayInputProps;
export type DisplayTextProps = DisplayTextareaProps;
export type DisplayTimeProps = FieldTimeProps;
export type DisplayYearProps = DisplayNumberInputProps;

// Attachments
export const DisplayAttachmentsBase = ({
  ref,
  ...props
}: DisplayAttachmentsProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const accessor = useCallback(
    (value: unknown) =>
      Array.isArray(value) && value.length
        ? `${value.length} files`
        : undefined,
    []
  );

  return <DisplayInput ref={ref} accessor={accessor} {...props} />;
};

// Array
export const DisplayArrayBase = ({
  ref,
  ...props
}: DisplayArrayProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const accessor = useCallback(
    (value: unknown) =>
      Array.isArray(value)
        ? value.map((v) => String(v))?.join(', ')
        : undefined,
    []
  );

  return <DisplayInput ref={ref} accessor={accessor} {...props} />;
};

// Array Reference
export const DisplayArrayReferenceBase = ({
  ref,
  ...props
}: DisplayArrayReferenceProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const accessor = useCallback(
    (value: unknown) =>
      Array.isArray(value)
        ? value.map((v: { display_name: string }) => v.display_name)?.join(', ')
        : undefined,
    []
  );

  return <DisplayInput ref={ref} accessor={accessor} {...props} />;
};

// Boolean
export const DisplayBooleanBase = ({
  accessor,
  path,
  ref,
  ...props
}: DisplayBooleanProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const {
    field: { value: fieldValue }
  } = useController<{ [key: string]: boolean | string | null | undefined }>({
    name: path
  });

  const value = useMemo((): boolean | null => {
    const accessedValue = accessor ? accessor(fieldValue) : fieldValue;

    if (typeof accessedValue === 'boolean') return accessedValue;
    if (typeof accessedValue !== 'string') return null;

    const normalized = accessedValue.toLowerCase().trim();
    return normalized === 'true' ? true : normalized === 'false' ? false : null;
  }, [accessor, fieldValue]);

  const { children, ...restProps } = props;

  return (
    <Checkbox
      ref={ref}
      isIndeterminate={value == null}
      isSelected={value || false}
      {...restProps}
    >
      <CheckboxContent>
        <CheckboxControl>
          <CheckboxIndicator />
        </CheckboxControl>
        {children as React.ReactNode}
      </CheckboxContent>
    </Checkbox>
  );
};

// Currency
export const DisplayCurrencyBase = ({
  ref,
  ...props
}: DisplayCurrencyProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <DisplayNumberInput
      ref={ref}
      formatOptions={{
        style: 'currency',
        currency: 'USD'
      }}
      {...props}
    />
  );
};

// Date and Time
export const DisplayDateTimeBase = ({
  ref,
  ...props
}: DisplayDateTimeProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <FieldDatePicker ref={ref} isReadOnly granularity="second" {...props} />
  );
};

// Date
export const DisplayDateBase = (props: DisplayDateProps) => (
  <FieldDatePicker isReadOnly granularity="day" parse={parseDate} {...props} />
);

// Enum
const humanizeEnum = (value: unknown) =>
  typeof value === 'string'
    ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

export const DisplayEnumBase = (props: DisplayEnumProps) => (
  <DisplayInput accessor={humanizeEnum} {...props} />
);

// Float
export const DisplayFloatBase = ({
  ref,
  ...props
}: DisplayFloatProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <DisplayNumberInput ref={ref} {...props} />
);

// Image
export const DisplayImageBase = ({
  accessor,
  ref,
  ...props
}: DisplayImageProps & { ref?: React.Ref<HTMLImageElement> }) => {
  const { path } = props;
  const {
    field: { value: fieldValue }
  } = useController<{ [key: string]: unknown }>({
    name: path
  });

  const value = useMemo(
    () =>
      accessor
        ? accessor(fieldValue)
        : (fieldValue as string | null | undefined),
    [accessor, fieldValue]
  );

  return (
    <img ref={ref} className="rounded-lg" src={value ?? undefined} {...props} />
  );
};

// Integer
export const DisplayIntegerBase = (props: DisplayIntegerProps) => (
  <DisplayFloatBase {...props} />
);

// Link
export const DisplayLinkBase = ({
  accessor,
  children,
  empty,
  ref,
  ...props
}: DisplayLinkProps & { ref?: React.Ref<HTMLAnchorElement> }) => {
  const { path } = props;
  const {
    field: { value: fieldValue }
  } = useController<{ [key: string]: string | null | undefined }>({
    name: path
  });

  const value = useMemo(
    () => (accessor ? accessor(fieldValue) : fieldValue),
    [accessor, fieldValue]
  );

  return (
    <>
      {value ? (
        <Link
          ref={ref}
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children || value}
          <Icon icon="bi:box-arrow-up-right" className="ml-1 inline size-3" />
        </Link>
      ) : (
        <div>{empty}</div>
      )}
    </>
  );
};

// Reference
export const DisplayReferenceBase = ({
  ref,
  ...props
}: DisplayReferenceProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const accessor = useCallback(
    (value: unknown) => (value as { display_name?: string })?.display_name,
    []
  );

  return <DisplayInput ref={ref} accessor={accessor} {...props} />;
};

// String
export const DisplayStringBase = ({
  ref,
  ...props
}: DisplayStringProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <DisplayInput ref={ref} {...props} />;
};

// Text
export const DisplayTextBase = ({
  ref,
  ...props
}: DisplayTextProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <DisplayTextarea ref={ref} {...props} />;
};

// Time
export const DisplayTimeBase = ({
  ref,
  ...props
}: DisplayTimeProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FieldTimeInput ref={ref} isReadOnly {...props} />;
};

// Year
export const DisplayYearBase = ({
  ref,
  ...props
}: DisplayYearProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <DisplayNumberInput
      ref={ref}
      formatOptions={{
        maximumFractionDigits: 0,
        useGrouping: false
      }}
      {...props}
    />
  );
};

// Overrideable component exports
export const DisplayAttachments = (props: DisplayAttachmentsProps) =>
  useGlobalComponent('DisplayAttachments', DisplayAttachmentsBase, props);

export const DisplayArrayReference = (props: DisplayArrayReferenceProps) =>
  useGlobalComponent('DisplayArrayReference', DisplayArrayReferenceBase, props);

export const DisplayArray = (props: DisplayArrayProps) =>
  useGlobalComponent('DisplayArray', DisplayArrayBase, props);

export const DisplayBoolean = (props: DisplayBooleanProps) =>
  useGlobalComponent('DisplayBoolean', DisplayBooleanBase, props);

export const DisplayCurrency = (props: DisplayCurrencyProps) =>
  useGlobalComponent('DisplayCurrency', DisplayCurrencyBase, props);

export const DisplayDateTime = (props: DisplayDateTimeProps) =>
  useGlobalComponent('DisplayDateTime', DisplayDateTimeBase, props);

export const DisplayDate = (props: DisplayDateProps) =>
  useGlobalComponent('DisplayDate', DisplayDateBase, props);

export const DisplayEnum = (props: DisplayEnumProps) =>
  useGlobalComponent('DisplayEnum', DisplayEnumBase, props);

export const DisplayFloat = (props: DisplayFloatProps) =>
  useGlobalComponent('DisplayFloat', DisplayFloatBase, props);

export const DisplayImage = (props: DisplayImageProps) =>
  useGlobalComponent('DisplayImage', DisplayImageBase, props);

export const DisplayInteger = (props: DisplayIntegerProps) =>
  useGlobalComponent('DisplayInteger', DisplayIntegerBase, props);

export const DisplayLink = (props: DisplayLinkProps) =>
  useGlobalComponent('DisplayLink', DisplayLinkBase, props);

export const DisplayReference = (props: DisplayReferenceProps) =>
  useGlobalComponent('DisplayReference', DisplayReferenceBase, props);

export const DisplayString = (props: DisplayStringProps) =>
  useGlobalComponent('DisplayString', DisplayStringBase, props);

export const DisplayText = (props: DisplayTextProps) =>
  useGlobalComponent('DisplayText', DisplayTextBase, props);

export const DisplayTime = (props: DisplayTimeProps) =>
  useGlobalComponent('DisplayTime', DisplayTimeBase, props);

export const DisplayYear = (props: DisplayYearProps) =>
  useGlobalComponent('DisplayYear', DisplayYearBase, props);
